/**
 * Pool service
 * Handles all pool-related API calls
 */

import { API_ENDPOINTS } from './endpoints';
import { fetchApi } from '@/lib/utils/api';
import { showSuccessToast } from '@/lib/utils/toast';
import type { Pool, Player, PlayerRole } from '@/lib/types/auction';

interface CreatePoolInput {
  name: string;
  basePrice: number;
  playerRole: PlayerRole;
  description?: string;
}

interface UpdatePoolInput extends Partial<CreatePoolInput> {}

export const poolService = {
  /**
   * Create new pool
   */
  async create(tournamentId: string, data: CreatePoolInput): Promise<Pool> {
    const response = await fetchApi<Pool>(API_ENDPOINTS.POOLS.BASE, {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        tournamentId,
      }),
    });
    showSuccessToast('pool', 'create');
    return response.result;
  },

  /**
   * Update pool
   */
  async update(id: string, data: UpdatePoolInput): Promise<Pool> {
    const response = await fetchApi<Pool>(`${API_ENDPOINTS.POOLS.BASE}/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        ...data,
      }),
    });
    showSuccessToast('pool', 'update');
    return response.result;
  },

  /**
   * Get pool by ID
   */
  async getById(id: string): Promise<Pool> {
    const response = await fetchApi<Pool>(`${API_ENDPOINTS.POOLS.BASE}/${id}`);
    return response.result;
  },

  /**
   * Get pool players
   */
  async getPlayers(poolId: string): Promise<Player[]> {
    const response = await fetchApi<Player[]>(
      API_ENDPOINTS.POOLS.PLAYERS(poolId)
    );
    return response.result;
  },

  /**
   * Get pool rules
   */
  async getRules(poolId: string): Promise<string[]> {
    const response = await fetchApi<string[]>(
      API_ENDPOINTS.POOLS.RULES(poolId)
    );
    return response.result;
  },

  /**
   * Assign player to pool
   */
  async assignPoolToPlayer(poolId: string, playerId: string): Promise<Player> {
    const response = await fetchApi<Player>(
      `${API_ENDPOINTS.POOLS.BASE}/${poolId}/players/${playerId}`,
      {
        method: 'POST',
      }
    );
    showSuccessToast('player', 'create');
    return response.result;
  },

  /**
   * Remove player from pool
   */
  async removePlayerFromPool(poolId: string, playerId: string): Promise<void> {
    await fetchApi<void>(
      `${API_ENDPOINTS.POOLS.BASE}/${poolId}/players/${playerId}`,
      {
        method: 'DELETE',
      }
    );
    showSuccessToast('player', 'delete');
  },

  /**
   * Get all pools for a tournament
   */
  async getPools(tournamentId: string): Promise<Pool[]> {
    const response = await fetchApi<Pool[]>(
      `${API_ENDPOINTS.POOLS.BASE}/tournament/${tournamentId}`
    );
    return response.result;
  },
};
